import { forwardRef, useCallback, useEffect, useId, useRef, useState, type InputHTMLAttributes } from 'react'
import { cn } from '../../utils.js'

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Label for accessibility (visually hidden by default) */
  label?: string
  /** Show label visually */
  showLabel?: boolean
  /** Debounce delay in ms for the onChange callback */
  debounceMs?: number
  /** Called with the debounced search value */
  onChange?: (value: string) => void
  /** Called on every keystroke without debounce */
  onChangeImmediate?: (value: string) => void
}

/**
 * Search input with a search icon, clear button, and optional debounced onChange.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    label = 'Search',
    showLabel = false,
    debounceMs = 300,
    onChange,
    onChangeImmediate,
    className,
    id: externalId,
    defaultValue = '',
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const id = externalId ?? generatedId
  const [value, setValue] = useState(String(defaultValue))
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setValue(newValue)
      onChangeImmediate?.(newValue)

      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onChange?.(newValue)
      }, debounceMs)
    },
    [onChange, onChangeImmediate, debounceMs],
  )

  const handleClear = useCallback(() => {
    setValue('')
    onChangeImmediate?.('')
    onChange?.('')
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [onChange, onChangeImmediate])

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'text-sm font-medium text-[var(--color-text)]',
          !showLabel && 'sr-only',
        )}
      >
        {label}
      </label>
      <div className="relative">
        {/* Search icon */}
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
        <input
          ref={ref}
          id={id}
          type="search"
          role="searchbox"
          value={value}
          onChange={handleChange}
          className={cn(
            'w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text)]',
            'placeholder:text-[var(--color-text-muted)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors px-3 py-2 pl-9 pr-9 text-sm',
            className,
          )}
          {...props}
        />
        {/* Clear button */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)] transition-colors"
          >
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
})
